import { LookupOportunidade } from '@shared/services/models/vendas/oportunidade.model';
import { OportunidadeKanbanItemComValorStruct } from '@shared/services/structs/vendas/oportunidade-kanban-item-com-valor.struct';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { ContatoPessoaStruct } from '../../../shared/services/structs/pessoa/contato-pessoa.struct';
import { VendedorLookupStruct } from '@shared/services/structs/pessoa/vendedor-lookup.struct';
import {
  AVATAR_COLORS,
  OportunidadeCard,
  PRIORIDADE_POR_ID,
} from './crm-oportunidades.models';

/**
 * Converte o que a API devolve no OportunidadeCard que o board já usa.
 *
 * O card é a forma interna da tela e não muda: assim o template, os filtros e a ordenação
 * seguem intactos ao trocar mock por API.
 *
 * Nomes de cliente/contato e de vendedor responsável não vêm do CRM — vivem em db_pessoa.
 * Chegam por mapa, montado no componente a partir dos lookups dessas APIs.
 */
export interface ContextoMapeamento {
  pessoasPorId: Map<number, string>;
  /** pessoa_contato, não pessoa: contato é a pessoa de contato do cliente. */
  contatosPorId: Map<number, ContatoPessoaStruct>;
  vendedoresPorId: Map<number, VendedorLookupStruct>;
  origensPorId: Map<number, string>;
}

export function mapaDeLookup(lista: LookupOportunidade[]): Map<number, string> {
  return new Map(lista.map(item => [item.id, item.descricao]));
}

/**
 * Nome do vendedor como o CRM exibe: com a marca de inativo (RV-06). Vendedor que não existe no
 * cadastro (oportunidade anterior à troca de usuário por vendedor, gravada com 0) aparece como "—".
 */
export function nomeDoVendedor(vendedor: VendedorLookupStruct | undefined): string {
  if (!vendedor) return '—';
  return vendedor.idSituacaoVendedor === SituacaoVendedorEnum.Inativo ? `${vendedor.nome} (inativo)` : vendedor.nome;
}

export function iniciaisDoNome(nome: string): string {
  // Corta no separador antes de olhar as partes: "Felipe Lima - Master" é nome mais
  // qualificador, e primeira+última sobre o texto inteiro dava "FM" em vez de "FL".
  const semQualificador = (nome || '').split(/\s+-\s+/)[0];

  const partes = semQualificador.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/** Cor do avatar derivada do nome: o mesmo vendedor tem sempre a mesma cor. */
export function corDoNome(nome: string): string {
  let soma = 0;
  for (const caractere of nome || '') soma = (soma + caractere.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[soma];
}

/** dd/MM/yyyy — o formato que o card exibe e que os filtros de data comparam. */
/**
 * Converte data-hora vinda da API em Date.
 *
 * A API grava DateTime.UtcNow em coluna `timestamp` (sem fuso) e serializa sem o sufixo "Z".
 * O JavaScript lê string data-hora sem fuso como **hora local**, então o valor exibido era a
 * data em UTC, não a do usuário: depois das 21h de Brasília o dia virava o seguinte. Marcar
 * como UTC devolve o instante correto, que o toLocaleDateString então mostra no fuso de quem
 * está olhando.
 *
 * A data pura ("yyyy-MM-dd", como data_prevista da ação) é montada como data local: o JS já
 * a interpreta como meia-noite UTC, o que a jogaria para o dia anterior a oeste de Greenwich.
 */
export function dataDaApi(iso: string): Date {
  const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (soData) {
    return new Date(Number(soData[1]), Number(soData[2]) - 1, Number(soData[3]));
  }

  const temFuso = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(temFuso ? iso : `${iso}Z`);
}

export function formatarDataIso(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const data = dataDaApi(iso);
  if (Number.isNaN(data.getTime())) return null;

  const dia = `${data.getDate()}`.padStart(2, '0');
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');
  return `${dia}/${mes}/${data.getFullYear()}`;
}

export function mapearCard(item: OportunidadeKanbanItemComValorStruct,
  contexto: ContextoMapeamento): OportunidadeCard {
  const vendedor = contexto.vendedoresPorId.get(item.idVendedorResponsavel);
  const contato = item.idPessoaContato ? contexto.contatosPorId.get(item.idPessoaContato) : undefined;

  return {
    // "#12", sem zeros à esquerda — mesma regra do formatCodigoCompra de Compras.
    id: `#${item.codigo}`,
    idOportunidade: item.idOportunidade,
    stage: item.chaveEtapa,
    idEtapaKanban: item.idEtapaKanban,

    titulo: item.titulo,

    idCliente: item.idPessoaCliente,
    cliente: contexto.pessoasPorId.get(item.idPessoaCliente) ?? `Cliente ${item.idPessoaCliente}`,
    idContato: item.idPessoaContato ?? null,
    contato: contato?.descricao ?? null,
    contatoEmail: contato?.email ?? null,

    idVendedorResponsavel: item.idVendedorResponsavel,
    resp: iniciaisDoNome(vendedor?.nome ?? ''),
    respName: nomeDoVendedor(vendedor),
    respColor: corDoNome(vendedor?.nome ?? ''),
    idUsuarioInclusao: item.idUsuarioInclusao ?? null,
    datetimeInclusao: item.datetimeInclusao,
    // Ação antiga gravada com 0 conta como sem responsável: o padrão (usuário logado) assume.
    idUsuarioResponsavelAcao: item.proximaAcao?.idUsuarioResponsavel || null,

    origem: contexto.origensPorId.get(item.idOrigemLead) ?? '',
    idOrigemLead: item.idOrigemLead,
    idCanalContato: item.idCanalContato,

    valorEstimado: item.valorEstimado ?? 0,
    valor: item.valor ?? item.valorEstimado ?? 0,
    idVendaValor: item.idVendaValor ?? null,
    vendaTipoDocumentoValor: item.vendaTipoDocumentoValor ?? null,
    numeroDocumentoValor: item.numeroDocumentoValor ?? null,
    prioridade: PRIORIDADE_POR_ID[item.idTipoPrioridade] ?? 'media',

    proximaAcao: item.proximaAcao?.descricao ?? null,
    dataProximaAcao: formatarDataIso(item.proximaAcao?.dataPrevista),

    tags: item.tags ?? [],
    arquivado: item.isArquivado,
    isFavorito: item.isFavorito,
    // datetimeInclusao já vem na hora da empresa: só a parte da data, sem conversão de fuso.
    criadoEm: formatarDataIso(item.datetimeInclusao?.substring(0, 10)) ?? '',

    // Preenchido só quando a ficha do card é aberta (GET /Oportunidade/{id}).
    camposValores: {},

    // Descrições das opções escolhidas no encerramento, já resolvidas pela API.
    resultado: item.descricaoResultado ?? undefined,
    motivo: item.descricaoMotivo ?? undefined,
    idCampoDefinicaoOpcaoResultado: item.idCampoDefinicaoOpcaoResultado,
    idCampoDefinicaoOpcaoMotivo: item.idCampoDefinicaoOpcaoMotivo,
  };
}

/** De onde vem o valor do card: "Valor do orçamento nº X", "Valor do pedido nº X" ou "Valor estimado". */
export function origemDoValor(card: OportunidadeCard): string {
  if (card.idVendaValor == null) return 'Valor estimado';
  return card.vendaTipoDocumentoValor === VendaTipoDocumentoEnum.PedidoDeVenda
    ? `Valor do pedido nº ${card.numeroDocumentoValor ?? ''}`
    : `Valor do orçamento nº ${card.numeroDocumentoValor ?? ''}`;
}

/** Ícone da origem do valor, ao lado do valor no card e na lista. */
export function iconeDoValor(card: OportunidadeCard): string {
  if (card.idVendaValor == null) return 'edit_note';
  return card.vendaTipoDocumentoValor === VendaTipoDocumentoEnum.PedidoDeVenda ? 'receipt_long' : 'request_quote';
}

/**
 * Reaplica no card os dados que só vêm no detalhe. Muta o card existente em vez de trocar a
 * referência: o board guarda essa referência em cardsByStage e listaCards.
 */
export function aplicarDetalhe(card: OportunidadeCard,
  campos: { idCampoDefinicao: number; valor: string | null; idsOpcaoSelecionada: number[] }[],
  observacoesFinais: string | null): void {
  const valores: Record<number, string> = {};
  for (const campo of campos) {
    if (campo.idsOpcaoSelecionada.length) {
      valores[campo.idCampoDefinicao] = campo.idsOpcaoSelecionada.join(',');
    } else if (campo.valor != null) {
      valores[campo.idCampoDefinicao] = campo.valor;
    }
  }

  card.camposValores = valores;
  card.observacoesFinais = observacoesFinais ?? undefined;
}
